import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {MoreHorizontalLine} from '@dropbox/dig-icons/assets';
import {snackbarAtom} from 'atoms/snackbar';
import type {WorkstreamUpdate} from 'client';
import {CancelButton} from 'components/DSYS/CancelButton';
import {DeleteModal} from 'components/DSYS/DeleteModal';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {type ComponentProps, useEffect, useState} from 'react';
import {useWorkstreamStatus} from 'views/workstreams/hooks';
import {WorkstreamUpdateStatusSelect} from 'views/workstreams/WorkstreamUpdateCellContent';

import {UpdatePost, updateValue} from './UpdateCard';
import {useHoverReveal} from './useHoverReveal';
import {logWorkstreamsInteraction} from './workstreamsAnalytics';

/**
 * The detail page's rule: the latest update (and its summary) is editable,
 * even once the next one is due. Cards in the list don't edit.
 */
export const canEditUpdate = (
  workstream: {updates?: WorkstreamUpdate[]},
  update: WorkstreamUpdate
) => update.id === workstream.updates?.[0]?.id;

// The /workstreams cell's inline edit: status select, the update in an editor,
// Cancel / Save.
const UpdateEditForm = ({update, onDone}: {update: WorkstreamUpdate; onDone: () => void}) => {
  const {editUpdate, isEditing: isSaving} = useWorkstreamStatus();
  const setSnackbar = useSetAtom(snackbarAtom);
  const [status, setStatus] = useState(update.status);
  const [comment, setComment] = useState(update.comment);
  const canSave = status !== update.status || comment !== update.comment;

  const save = async () => {
    await editUpdate({update: {...update, comment, status}});
    logWorkstreamsInteraction('update_edit_saved', {
      workstream_id: update.workstream_id,
      status_changed: status !== update.status,
      comment_changed: comment !== update.comment,
    });
    setSnackbar({text: t('saved')});
    onDone();
  };

  return (
    <Stack gap="8">
      <Box display="flex" alignItems="center">
        <WorkstreamUpdateStatusSelect
          workstreamId={update.workstream_id}
          status={status}
          defaultStatus={update.status}
          onStatusChange={setStatus}
        />
      </Box>
      <RichTextArea
        editable
        autoFocus
        value={updateValue(update)}
        onChange={(value) => setComment(value.toString())}
        textSize="small"
        squareBorders
        disableTables
        source="workstreams4-update-edit"
      />
      <Box display="flex" justifyContent="flex-end" style={{gap: 8}}>
        <CancelButton size="small" onClick={onDone}>
          {t('cancel')}
        </CancelButton>
        <Button
          size="small"
          variant="primary"
          onClick={save}
          disabled={!canSave}
          isLoading={isSaving}
        >
          {t('save')}
        </Button>
      </Box>
    </Stack>
  );
};

// The /workstreams overflow's Edit update / Delete, the delete behind the same
// confirm modal (which shows the snackbar and closes itself).
const UpdateOverflow = ({
  update,
  onEdit,
  onOpenChange,
}: {
  update: WorkstreamUpdate;
  onEdit: () => void;
  onOpenChange: (open: boolean) => void;
}) => {
  const {deleteUpdate, isDeleting} = useWorkstreamStatus();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => onOpenChange(menuOpen || confirming), [menuOpen, confirming, onOpenChange]);

  const handleDelete = async () => {
    await deleteUpdate({id: update.id, workstreamId: update.workstream_id});
    logWorkstreamsInteraction('update_deleted', {workstream_id: update.workstream_id});
  };

  return (
    <>
      <DeleteModal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={t('discard_workstream_update')}
        subtitle={t('discard_workstream_update_helper')}
        isPending={isDeleting}
        onDelete={handleDelete}
      />
      <Menu.Wrapper
        onToggle={({isOpen}) => {
          if (isOpen) {
            logWorkstreamsInteraction('update_menu_opened', {workstream_id: update.workstream_id});
          }
          setMenuOpen(isOpen);
        }}
      >
        {({getContentProps, getTriggerProps}) => (
          <>
            <IconButton
              {...getTriggerProps()}
              aria-label={t('more_options').toString()}
              variant="borderless"
              size="small"
            >
              <UIIcon src={MoreHorizontalLine} size="small" />
            </IconButton>
            <Menu.Content {...getContentProps()} minWidth="160px" placement="bottom-end">
              <Menu.Segment>
                <Menu.ActionItem
                  onClick={() => {
                    logWorkstreamsInteraction('update_edit_opened', {
                      workstream_id: update.workstream_id,
                    });
                    onEdit();
                  }}
                >
                  {t('edit_update')}
                </Menu.ActionItem>
                <Menu.ActionItem
                  onClick={() => {
                    // Against update_deleted, the deletes cancelled at the modal.
                    logWorkstreamsInteraction('update_delete_clicked', {
                      workstream_id: update.workstream_id,
                    });
                    setConfirming(true);
                  }}
                >
                  {t('delete')}
                </Menu.ActionItem>
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    </>
  );
};

/**
 * /workstreams: UpdatePost with a ··· overflow (Edit update /
 * Delete, as on /workstreams) at the byline's end when `editable` (see
 * canEditUpdate), shown while the post is hovered or holds keyboard focus, and
 * while its menu or delete confirm is open; Edit swaps the post for the edit
 * form.
 */
export const EditableUpdatePost = ({
  editable,
  ...postProps
}: ComponentProps<typeof UpdatePost> & {editable: boolean}) => {
  const [editing, setEditing] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const {regionProps, revealStyle} = useHoverReveal();

  if (editing && editable) {
    return <UpdateEditForm update={postProps.update} onDone={() => setEditing(false)} />;
  }
  return (
    <Box {...regionProps}>
      <UpdatePost
        {...postProps}
        action={
          editable ? (
            // Held to the byline's 20px line, so the 32px button overflows it
            // rather than making the editable (latest) post's byline taller.
            <Box
              display="flex"
              alignItems="center"
              style={{...revealStyle, ...(overflowOpen && {opacity: 1}), height: 20}}
            >
              <UpdateOverflow
                update={postProps.update}
                onEdit={() => setEditing(true)}
                onOpenChange={setOverflowOpen}
              />
            </Box>
          ) : undefined
        }
      />
    </Box>
  );
};
